/**
 * WCAG 2.1 contrast maths.
 *
 * A pure module with its own tests, per `docs/roadmap.md` §7 invariant 5 and
 * `claude-kit/rules/working-style.md` ("Business rules live in pure, tested modules"). It is the
 * only place in the web app that decides whether two colours are legible together, so the theme
 * guard, the palette test, and anything that later validates a tenant's brand colour cannot each
 * arrive at a different answer.
 *
 * Deliberately does not pick a foreground for you by luminance threshold — a threshold can choose
 * an ink that a real ratio comparison would reject in favour of one that actually measures higher
 * (the regression `contrast.test.ts` guards against: an amber brand colour shipping at 3.05:1
 * against white when near-black measures 6.21:1 against the same amber). The decision here is
 * always "compare both actual ratios", which is what `betterForeground` does.
 */

/** WCAG AA for normal-size body text. */
export const AA_TEXT = 4.5;
/** WCAG AA for large text (>=24px, or >=18.66px bold) and for non-text UI boundaries. */
export const AA_LARGE = 3;

export interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

const HEX_SHORT = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const HEX_LONG = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const RGB_FUNC = /^rgba?\(\s*([0-9.]+)[\s,]+([0-9.]+)[\s,]+([0-9.]+)/i;

function channel(text: string, radix: number): number | null {
  const value = Number.parseInt(text, radix);
  return Number.isNaN(value) ? null : value;
}

/**
 * Parse a CSS colour into 8-bit sRGB channels, or `null` if it is not one this module understands.
 *
 * Accepts `#rgb`, `#rrggbb`, and the `rgb()` / `rgba()` form that `getComputedStyle` returns —
 * which is the only form the runtime theme guard ever sees, because the browser resolves a `var()`
 * chain to a computed value before handing it back.
 *
 * Returns `null` rather than throwing or guessing. A caller that cannot parse a colour must treat
 * that as a failed check, never as a pass: `guard.ts` does exactly that.
 */
export function parseColor(value: string): Rgb | null {
  const text = value.trim();

  const short = HEX_SHORT.exec(text);
  if (short !== null) {
    const [, r, g, b] = short;
    if (r === undefined || g === undefined || b === undefined) return null;
    const rr = channel(r + r, 16);
    const gg = channel(g + g, 16);
    const bb = channel(b + b, 16);
    if (rr === null || gg === null || bb === null) return null;
    return { r: rr, g: gg, b: bb };
  }

  const long = HEX_LONG.exec(text);
  if (long !== null) {
    const [, r, g, b] = long;
    if (r === undefined || g === undefined || b === undefined) return null;
    const rr = channel(r, 16);
    const gg = channel(g, 16);
    const bb = channel(b, 16);
    if (rr === null || gg === null || bb === null) return null;
    return { r: rr, g: gg, b: bb };
  }

  const func = RGB_FUNC.exec(text);
  if (func !== null) {
    const [, r, g, b] = func;
    if (r === undefined || g === undefined || b === undefined) return null;
    const rr = Number.parseFloat(r);
    const gg = Number.parseFloat(g);
    const bb = Number.parseFloat(b);
    if (Number.isNaN(rr) || Number.isNaN(gg) || Number.isNaN(bb)) return null;
    return { r: rr, g: gg, b: bb };
  }

  return null;
}

function linearize(component: number): number {
  const c = component / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Relative luminance, per WCAG 2.1 §relative-luminance. */
export function relativeLuminance(color: Rgb): number {
  return 0.2126 * linearize(color.r) + 0.7152 * linearize(color.g) + 0.0722 * linearize(color.b);
}

/**
 * Contrast ratio between two colours, 1 to 21.
 *
 * Returns `null` when either colour cannot be parsed, so an unparseable value can never be
 * mistaken for a passing ratio. Both arguments are treated symmetrically — the lighter of the two
 * is always the numerator, as WCAG defines it.
 */
export function contrastRatio(a: string, b: string): number | null {
  const first = parseColor(a);
  const second = parseColor(b);
  if (first === null || second === null) return null;

  const lumA = relativeLuminance(first);
  const lumB = relativeLuminance(second);
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Whichever of two candidate foregrounds actually reads better on `background`.
 *
 * **Not consumed by anything yet, and said so rather than left to be assumed.**
 * `docs/comparison-emrx.md` §2 is about exactly this — a declared capability nobody reads, which
 * "looks enforced and is not". It is kept because the tenant-branding requirement in
 * `claude-kit/rules/invariants.md` needs it the moment a practice supplies its own hex, and
 * because the test beside it records a specific bug worth not repeating: an amber brand colour
 * shipping with white ink at 3.05:1 because a luminance *threshold* chose the foreground, when
 * comparing the two real ratios would have picked near-black at 6.21:1.
 */
export function betterForeground(
  background: string,
  first: string,
  second: string,
): { readonly color: string; readonly ratio: number } | null {
  const firstRatio = contrastRatio(background, first);
  const secondRatio = contrastRatio(background, second);
  if (firstRatio === null || secondRatio === null) return null;
  return firstRatio >= secondRatio
    ? { color: first, ratio: firstRatio }
    : { color: second, ratio: secondRatio };
}
